import { EmpreintesCarboneData } from '@services/interfaces/generated/YSER-v1';
import { TransformBaseType, transformUnit } from '@shared/misc/helpers/data';
import { useMemo } from 'react';
import { EmpreintesCarboneDatas, UseEmpreintesCarboneTelephonesProps } from './type';

const useData = (data?: EmpreintesCarboneData): UseEmpreintesCarboneTelephonesProps => {
  // Filtrer les 12 derniers mois par défaut
  const filteredData = useMemo(() => {
    if (!data?.details) return undefined;

    const dateLimit = new Date();
    dateLimit.setMonth(dateLimit.getMonth() - 12); // 12 derniers mois

    const filtered = data.details.filter((item) => new Date(item.dateAchat) >= dateLimit);

    return {
      ...data,
      data: {
        ...data,
        details: filtered,
      },
    };
  }, [data]);

  const selectedUnit = useMemo<TransformBaseType>(() => {
    const tabNumbersCo2 = filteredData?.data?.details?.map((item) => item.totalEmpreinteCarbone) ?? [];
    const valeurCo2 = Math.max(...tabNumbersCo2, 0);
    const tempUnit = transformUnit(valeurCo2, 'co2eq');
    return tempUnit;
  }, [filteredData?.data?.details]);

  const empreinteData = useMemo<EmpreintesCarboneDatas[] | undefined>(() => {
    // Générer les 12 derniers mois glissants
    const now = new Date();
    const last12Months: { dateKey: string; timestamp: number }[] = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = date.toLocaleString('default', { month: 'short' }).replace('.', '');
      const year = date.getFullYear() % 100;
      const dateKey = `${month.charAt(0).toUpperCase() + month.slice(1)}.${year}`;
      last12Months.push({ dateKey, timestamp: date.getTime() });
    }

    // Grouper les données disponibles par mois
    const groupedByDate: Record<string, number> = {};

    if (filteredData?.data?.details && filteredData.data.details.length > 0) {
      filteredData.data.details.forEach((item) => {
        const date = new Date(item.dateAchat);
        const month = date.toLocaleString('default', { month: 'short' }).replace('.', '');
        const year = date.getFullYear() % 100;
        const dateKey = `${month.charAt(0).toUpperCase() + month.slice(1)}.${year}`;

        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = 0;
        }
        groupedByDate[dateKey] += item.totalEmpreinteCarbone;
      });
    }

    // Créer le tableau final avec tous les 12 mois
    return last12Months.map(({ dateKey }) => {
      const total = groupedByDate[dateKey] ?? 0;
      const valueWithSelectedUnit = transformUnit(total, 'co2eq', selectedUnit.index);
      return {
        x: dateKey,
        y: +valueWithSelectedUnit.value,
      };
    });
  }, [filteredData?.data?.details, selectedUnit.index]);

  return {
    data: filteredData,
    empreinteData,
    unitToShow: selectedUnit.unit,
    unitIndex: selectedUnit.index,
  };
};

export default useData;
